/**
 * A panel positioned against an anchor and rendered through a portal, because the table it
 * anchors to scrolls inside a clipping box.
 *
 * `role="tooltip"` with `aria-describedby` on the anchor, not `role="dialog"`: the panel is
 * purely informational, duplicates data already in the row, and traps nothing.
 */

import { useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useAnchoredPosition } from '~/hooks/useAnchoredPosition';
import { useDismissOnScroll } from '~/hooks/useDismissOnScroll';
import { useEscapeKey } from '~/hooks/useEscapeKey';
import styles from './AnchoredPanel.module.css';

export interface AnchoredPanelProps {
  id: string;
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  onDismiss: () => void;
  gap?: number;
  edge?: number;
  width?: number;
  children: ReactNode;
}

export function AnchoredPanel({
  id,
  anchorRef,
  open,
  onDismiss,
  gap,
  edge,
  width,
  children,
}: AnchoredPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const position = useAnchoredPosition({
    anchorRef,
    panelRef,
    open,
    ...(gap === undefined ? {} : { gap }),
    ...(edge === undefined ? {} : { edge }),
  });

  useDismissOnScroll(open, onDismiss);
  useEscapeKey(open, onDismiss);

  return createPortal(
    <div
      id={id}
      ref={panelRef}
      role="tooltip"
      className={[styles.panel, open ? styles.shown : styles.hidden].join(' ')}
      style={{ top: position.top, left: position.left, ...(width ? { width } : {}) }}
    >
      {children}
    </div>,
    document.body,
  );
}
